import { generateSignature } from "./generateSignature";
import { cleanSignatureImage } from "src/pages/pan/view/Dialog/PanFormDialogBox/signatureInk";
import { PcoFirm, findPcoFirm } from "./pcoFirms";

/**
 * "Details of the Individual issuing the certificate" plus the stamp the
 * Employer Certificate prints for them.
 *
 * PCO (167): a PcoFirm (pcoFirms.ts) fits this shape as it is — one combined
 * `sealSignatureImage` and an optional letterhead.
 * DSA (175): the agent who filed the application (agentIssuerDetails), with
 * their own `sealImage` and `signatureImage` from the DsaIssuer row, printed
 * signature over seal.
 */
export type CertificateIssuer = {
  fullName: string;
  designation: string;
  panNumber: string;
  officeAddressWithLocation: string;
  telephone: string;
  mobile: string;
  /** PCO: seal and signature as one image. */
  sealSignatureImage?: string;
  /** DSA: the agent's seal. */
  sealImage?: string;
  /** DSA: the agent's signature, drawn over the seal. */
  signatureImage?: string;
  /** PCO: blank letterhead the certificate is printed on. */
  letterheadImage?: string;
};

/** The issuer block for the agent who filed the application. */
export const agentIssuerDetails = (data: any) => {
  const isRetailer = data?.appliedByType === "RETAILER";
  const isDistributor = data?.appliedByType === "DISTRIBUTOR";
  return {
    fullName: isRetailer
      ? data?.retailerData?.name || data?.retailerName || "N/A"
      : isDistributor
      ? data?.distributorData?.name || "N/A"
      : "N/A",
    designation: "Proprietor",
    panNumber: isRetailer
      ? data?.retailerData?.panNumber || data?.retailerPanNumber || "N/A"
      : isDistributor
      ? data?.distributorData?.panNumber || "N/A"
      : "N/A",
    officeAddressWithLocation:
      data?.retailerData?.district || data?.distributorData?.district || "N/A",
    telephone: "N/A",
    mobile:
      data?.retailerMobileNumber || data?.distributorData?.mobileNumber || "N/A",
  };
};

export const isDsaCertificate = (data: any) => data?.baseCatAppied === "CAT_DSA";

/**
 * The DSA issuer: the agent's details, the seal of the Employer Certificate
 * Logo entry given to the application (`data.dsaLogo`) and the agent's own
 * signature (`data.dsaIssuer`), both from the employer-certificate reads.
 * `signatureImage` overrides the stored signature — resolveDsaSignature's
 * result. Null for anything that is not a DSA application.
 */
export const dsaIssuerFor = (
  data: any,
  signatureImage?: string,
  /** cleanDsaStamp's seal, over the stored one. */
  sealImage?: string
): CertificateIssuer | null => {
  if (!isDsaCertificate(data)) return null;
  return {
    ...agentIssuerDetails(data),
    sealImage: sealImage || data?.dsaLogo?.sealImage || "",
    signatureImage: signatureImage ?? data?.dsaIssuer?.signatureImage ?? "",
  };
};

/** The name the agent's signature is drawn from: their profile name. */
export const dsaSignerName = (data: any) => {
  const fromUser = Array.isArray(data?.user) ? data.user[0]?.name : "";
  const name = data?.appliedUser?.name || fromUser || agentIssuerDetails(data).fullName;
  return name === "N/A" ? "" : name;
};

type EnsureSignature = (args: {
  userId: string;
  image: string;
  name: string;
}) => Promise<any>;

/**
 * The signature a DSA certificate prints.
 *
 * 1. The agent's saved signature, when there is one.
 * 2. Otherwise one is generated from their profile name and saved through
 *    `ensure` (POST /dsa-issuer/ensure-signature). The URL that call returns
 *    is used — if another operator saved one a moment earlier, it is theirs,
 *    so both certificates match.
 * 3. If saving fails, the generated image prints on this certificate only and
 *    a warning is returned; the next certificate tries to save again.
 *
 * Never throws. `signatureImage` is "" when nothing could be made.
 */
export const resolveDsaSignature = async (
  data: any,
  ensure: EnsureSignature
): Promise<{ signatureImage: string; warning?: string }> => {
  const stored = data?.dsaIssuer?.signatureImage;
  if (stored) return { signatureImage: stored };

  const userId = data?.appliedById;
  let generated: { image: string; name: string };
  try {
    generated = await generateSignature(dsaSignerName(data));
  } catch {
    return {
      signatureImage: "",
      warning: "The agent's signature could not be generated, so the certificate has no signature.",
    };
  }
  if (userId) {
    try {
      const res: any = await ensure({ userId, image: generated.image, name: generated.name });
      const url = res?.data?.data?.signatureImage;
      if (url) return { signatureImage: url };
    } catch {
      // Falls through to the unsaved copy below.
    }
  }
  return {
    signatureImage: generated.image,
    warning: "The agent's signature was generated but could not be saved. It will be saved with the next certificate.",
  };
};

/**
 * The DSA seal and signature with their paper removed and trimmed to the ink
 * (cleanSignatureImage), as base64 PNGs.
 *
 * An uploaded seal / signature is usually a photo or scan on white. The
 * template's `mix-blend-mode: multiply` hides that white on screen, but the
 * PDF renderer ignores it, so the white printed as a box over the applicant's
 * photo and the text. An image that cannot be cleaned (no CORS, no ink) is
 * kept as it was.
 */
export const cleanDsaStamp = async (
  data: any,
  signatureImage?: string
): Promise<{ sealImage: string; signatureImage: string }> => {
  const seal = data?.dsaLogo?.sealImage || "";
  const sign = signatureImage ?? data?.dsaIssuer?.signatureImage ?? "";
  const [cleanSeal, cleanSign] = await Promise.all([
    removeBackground(seal),
    removeBackground(sign),
  ]);
  return { sealImage: cleanSeal, signatureImage: cleanSign };
};

/**
 * `src` with its paper removed — unless it is already see-through (a
 * generated signature, a transparent PNG upload), which is returned as it is:
 * the paper pass measures the background from the image, and on an image with
 * no paper it would take the ink itself for paper and fade it.
 */
export const removeBackground = async (src: string): Promise<string> => {
  if (!src || (await hasTransparency(src))) return src;
  return (await cleanSignatureImage(src)) || src;
};

/** True when any pixel is clearly see-through. False when it cannot be read. */
const hasTransparency = (src: string): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image();
    if (!src.startsWith("data:")) img.crossOrigin = "anonymous";
    img.onerror = () => resolve(false);
    img.onload = () => {
      try {
        const scale = Math.min(1, 300 / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(false);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const d = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        for (let i = 3; i < d.length; i += 4) {
          if (d[i] < 200) return resolve(true);
        }
        resolve(false);
      } catch {
        resolve(false);
      }
    };
    img.src = src;
  });

/**
 * The issuer a certificate prints: the agent (with their seal and signature)
 * on a DSA application, the PCO firm on a PCO one.
 *
 * DSA is decided first and never goes through findPcoFirm: that matches a PCO
 * firm name anywhere in the office address, and a DSA agent whose own firm
 * happened to share one of the five names would have printed that PCO firm's
 * details and seal.
 */
export const certificateIssuerFor = (
  data: any,
  pcoFirms: PcoFirm[] | undefined | null,
  officeAddress: string | undefined,
  dsaSignatureImage?: string,
  dsaSealImage?: string
): CertificateIssuer | null =>
  isDsaCertificate(data)
    ? dsaIssuerFor(data, dsaSignatureImage, dsaSealImage)
    : findPcoFirm(pcoFirms, officeAddress, data);
